import { array as codecArray, oneOf as codecOneOf } from 'purify-ts/Codec';
import { makeResponses } from '../response_factory';
import { utils as coreUtils, paginated, requestParams } from '@codegrade/api-core';
import { AllSiteSettings, CouponDataParser, CreateTenantData, ExtendedTenant, LTIProviderBase, NoPermissions, PatchRoleTenantData, PatchSettingsTenantData, PatchTenantData, PutPriceTenantData, Tenant, TenantCoupon, TenantPermissions, TenantPrice, TenantRoleAsJSONWithPerms, TenantStatistics, } from '@codegrade/api-models';
import { parsers } from '@codegrade/utils';
const makeResult = coreUtils.makeResult;
// eslint-disable-next-line @typescript-eslint/no-unused-vars,unused-imports/no-unused-vars
const parseJsonResponse = /* #__PURE__ */ parsers.parseJsonResponse;
export function tenant(config) {
    const createResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(ExtendedTenant)),
    });
    const getAllResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ paginated.makeResult(
        /* #__PURE__ */ parseJsonResponse(/* #__PURE__ */ codecArray(Tenant))),
    });
    const createTenantCouponResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(TenantCoupon)),
    });
    const deleteTenantCouponResponse = /* #__PURE__ */ makeResponses({
        204: /* #__PURE__ */ makeResult(async () => null),
    });
    const updateTenantCouponResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(TenantCoupon)),
    });
    const deletePriceResponse = /* #__PURE__ */ makeResponses({
        204: /* #__PURE__ */ makeResult(async () => null),
    });
    const putPriceResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(TenantPrice)),
    });
    const getAllTenantCouponsResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ paginated.makeResult(
        /* #__PURE__ */ parseJsonResponse(/* #__PURE__ */ codecArray(TenantCoupon))),
    });
    const getResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(ExtendedTenant)),
    });
    const patchResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(ExtendedTenant)),
    });
    const getLtiProvidersResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ paginated.makeResult(
        /* #__PURE__ */ parseJsonResponse(/* #__PURE__ */ codecArray(LTIProviderBase))),
    });
    const getPermissionsResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(
        /* #__PURE__ */ parseJsonResponse(
        /* #__PURE__ */ codecOneOf([TenantPermissions, NoPermissions]))),
    });
    const getRolesResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ paginated.makeResult(
        /* #__PURE__ */ parseJsonResponse(
        /* #__PURE__ */ codecArray(TenantRoleAsJSONWithPerms))),
    });
    const getSettingsResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(AllSiteSettings)),
    });
    const patchSettingsResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(AllSiteSettings)),
    });
    const getStatsResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(TenantStatistics)),
    });
    const patchRoleResponse = /* #__PURE__ */ makeResponses({
        204: /* #__PURE__ */ makeResult(async () => null),
    });
    return Object.freeze({
        /**
         * Create a new tenant.
         *
         * @param arg The arguments to the function.
         * @param arg.requestBody The body of the request.
         * @returns The newly created tenant.
         */
        create: async ({ requestBody, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/tenants/`, createResponse.v, JSON.stringify(CreateTenantData.encode(requestBody)));
            return result;
        },
        /**
         * Get all tenants of an instance.
         *
         * @param arg The arguments to the function.
         * @param arg.q Only find tenants with a name matching this search query.
         * @param arg.pageSize The size of a single page, maximum is 100.
         * @param arg.nextToken The next token, to retrieve a next page.
         * @returns All the tenants of this instance that the user is allowed to see.
         */
        getAll: async ({ q = undefined, pageSize = undefined, nextToken = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/tenants/`, getAllResponse.v, undefined, {
                q,
                'page-size': pageSize,
                'next-token': nextToken,
            });
            return result;
        },
        /**
         * Create a new coupon valid to redeem any course in a tenant, by Tenant id.
         *
         * @param arg The arguments to the function.
         * @param arg.tenantId The tenant that defines the scopes of the coupons.
         * @param arg.requestBody The body of the request.
         * @returns The coupon created for this tenant.
         */
        createTenantCoupon: async ({ tenantId, requestBody, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/tenants/${tenantId}/coupons`, createTenantCouponResponse.v, JSON.stringify(CouponDataParser.encode(requestBody)));
            return result;
        },
        /**
         * Delete a coupon.
         *
         * @param arg The arguments to the function.
         * @param arg.tenantId The id of the tenant price the coupon is connected to.
         * @param arg.couponId The id of the coupon you want to delete.
         * @returns Nothing
         */
        deleteTenantCoupon: async ({ tenantId, couponId, }) => {
            const result = await requestParams(config, 'DELETE', `/api/v1/tenants/${tenantId}/coupons/${couponId}`, deleteTenantCouponResponse.v, undefined);
            return result;
        },
        /**
         * Update the given coupon with new values.
         *
         * @param arg The arguments to the function.
         * @param arg.tenantId The tenant that defines the scopes of the coupons.
         * @param arg.couponId The id of the coupon you want to update.
         * @param arg.requestBody The body of the request.
         * @returns The updated coupon
         */
        updateTenantCoupon: async ({ tenantId, couponId, requestBody, }) => {
            const result = await requestParams(config, 'PATCH', `/api/v1/tenants/${tenantId}/coupons/${couponId}`, updateTenantCouponResponse.v, JSON.stringify(CouponDataParser.encode(requestBody)));
            return result;
        },
        /**
         * Update the price of the given course.
         *
         * @param arg The arguments to the function.
         * @param arg.tenantId The id of the tenant for which you want to delete the price.
         * @returns Nothing.
         */
        deletePrice: async ({ tenantId, }) => {
            const result = await requestParams(config, 'DELETE', `/api/v1/tenants/${tenantId}/price`, deletePriceResponse.v, undefined);
            return result;
        },
        /**
         * Update the price of the given course.
         *
         * @param arg The arguments to the function.
         * @param arg.tenantId The id of the tenant for which you want to update the price.
         * @param arg.requestBody The body of the request.
         * @returns The created or updated price.
         */
        putPrice: async ({ tenantId, requestBody, }) => {
            const result = await requestParams(config, 'PUT', `/api/v1/tenants/${tenantId}/price`, putPriceResponse.v, JSON.stringify(PutPriceTenantData.encode(requestBody)));
            return result;
        },
        /**
         * Retrieves all visible tenant coupon for the tenant linked to the tenant.
         *
         * @param arg The arguments to the function.
         * @param arg.tenantId The tenant that defines the scopes of the coupons.
         * @param arg.pageSize The size of a single page, maximum is 50.
         * @param arg.nextToken The next token, to retrieve a next page.
         * @returns The list of available coupons.
         */
        getAllTenantCoupons: async ({ tenantId, pageSize = undefined, nextToken = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/tenants/${tenantId}/coupons/`, getAllTenantCouponsResponse.v, undefined, {
                'page-size': pageSize,
                'next-token': nextToken,
            });
            return result;
        },
        /**
         * Get a tenant by id.
         *
         * @param arg The arguments to the function.
         * @param arg.tenantId The id of the tenant you want to retrieve.
         * @returns The tenant with the given id.
         */
        get: async ({ tenantId, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/tenants/${tenantId}`, getResponse.v, undefined);
            return result;
        },
        /**
         * Update a tenant by id.
         *
         * @param arg The arguments to the function.
         * @param arg.tenantId The id of the tenant you want to update.
         * @param arg.requestBody The body of the request.
         * @returns The updated tenant.
         */
        patch: async ({ tenantId, requestBody, }) => {
            const result = await requestParams(config, 'PATCH', `/api/v1/tenants/${tenantId}`, patchResponse.v, JSON.stringify(PatchTenantData.encode(requestBody)));
            return result;
        },
        /**
         * List all LTI providers for this tenant.
         *
         * @param arg The arguments to the function.
         * @param arg.tenantId The id of the tenant to get the LTI providers for.
         * @param arg.pageSize The size of a single page, maximum is 50.
         * @param arg.nextToken The next token, to retrieve a next page.
         * @returns A list of all known LTI providers.
         */
        getLtiProviders: async ({ tenantId, pageSize = undefined, nextToken = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/tenants/${tenantId}/lti_providers/`, getLtiProvidersResponse.v, undefined, {
                'page-size': pageSize,
                'next-token': nextToken,
            });
            return result;
        },
        /**
         * Get the permissions of the logged in user for this tenant.
         *
         * If the user does not have an associated role within the tenant, the global permissions of the user are returned.
         *
         * @param arg The arguments to the function.
         * @param arg.tenantId The tenant for which to get the permissions.
         * @returns The courses for this tenant.
         */
        getPermissions: async ({ tenantId, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/tenants/${tenantId}/permissions/`, getPermissionsResponse.v, undefined);
            return result;
        },
        /**
         * Get all roles of this tenant with their permissions
         *
         * @param arg The arguments to the function.
         * @param arg.tenantId The tenant to get the roles for.
         * @param arg.pageSize The size of a single page, maximum is 50.
         * @param arg.nextToken The next token, to retrieve a next page.
         * @returns An array of roles.
         */
        getRoles: async ({ tenantId, pageSize = undefined, nextToken = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/tenants/${tenantId}/roles/`, getRolesResponse.v, undefined, {
                'page-size': pageSize,
                'next-token': nextToken,
            });
            return result;
        },
        /**
         * Get the settings for this tenant.
         *
         * @param arg The arguments to the function.
         * @param arg.tenantId The tenant for which to get the settings.
         * @param arg.onlyFrontend Get only the frontend settings. When `True` the returned mapping will contain all frontend settings, even those that only have a global value.
         * @returns The settings for this tenant.
         */
        getSettings: async ({ tenantId, onlyFrontend = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/tenants/${tenantId}/settings/`, getSettingsResponse.v, undefined, {
                only_frontend: onlyFrontend,
            });
            return result;
        },
        /**
         * Update the settings for this tenant.
         *
         * @param arg The arguments to the function.
         * @param arg.tenantId The tenant to update the settings for.
         * @param arg.requestBody The body of the request.
         * @returns The updated tenant settings.
         */
        patchSettings: async ({ tenantId, requestBody, }) => {
            const result = await requestParams(config, 'PATCH', `/api/v1/tenants/${tenantId}/settings/`, patchSettingsResponse.v, JSON.stringify(PatchSettingsTenantData.encode(requestBody)));
            return result;
        },
        /**
         * Get the statistics of a tenant.
         *
         * @param arg The arguments to the function.
         * @param arg.tenantId The id of the tenant for which you want to get the statistics.
         * @returns The statistics of the specified tenant.
         */
        getStats: async ({ tenantId, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/tenants/${tenantId}/statistics/`, getStatsResponse.v, undefined);
            return result;
        },
        /**
         * Update the `Permission` of a given `TenantRole`.
         *
         * @param arg The arguments to the function.
         * @param arg.tenantId The tenant to patch the role for.
         * @param arg.roleId The id of the tenant role.
         * @param arg.requestBody The body of the request.
         * @returns An empty response with return code 204.
         */
        patchRole: async ({ tenantId, roleId, requestBody, }) => {
            const result = await requestParams(config, 'PATCH', `/api/v1/tenants/${tenantId}/roles/${roleId}`, patchRoleResponse.v, JSON.stringify(PatchRoleTenantData.encode(requestBody)));
            return result;
        },
    });
}
